import type { Subscriber } from 'rxjs';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { debounceTime, filter, map, take } from 'rxjs/operators';
import type { OneTrust } from '../types';

declare const window: Window;
export const OneTrust$: ReplaySubject<OneTrust> = new ReplaySubject<OneTrust>(1);
export const appliedLocale$: BehaviorSubject<string> = new BehaviorSubject('');

const MutationObserver$ = (
  target: Node,
  config: MutationObserverInit = { childList: true }
): Observable<MutationRecord> =>
  new Observable((observer: Subscriber<MutationRecord>) => {
    const mutationObserver: MutationObserver = new MutationObserver((mutations: MutationRecord[]): void => {
      mutations.forEach((mutation: MutationRecord): void => {
        observer.next(mutation);
      });
    });
    mutationObserver.observe(target, config);
    return (): void => {
      mutationObserver.disconnect();
    };
  });

const OneTrustReady$ = (): Observable<OneTrust> =>
  MutationObserver$(document.body).pipe(
    filter((record: MutationRecord): boolean => record.addedNodes.length === 1),
    map((record: MutationRecord) => record.addedNodes.item(0)),
    filter(
      (element: Node) =>
        !!element &&
        element instanceof HTMLElement && // there are some text elements that need skipping!
        element.getAttribute('id') === 'onetrust-consent-sdk'
    ),
    debounceTime(300),
    map((): OneTrust => window['OneTrust'])
  );

const initializeOneTrust = (
  src: string,
  domainScriptValue: string,
  documentBasedLang = true
  // countryCode?: string
): void => {
  const head: HTMLHeadElement = window.document.getElementsByTagName('head')[0];

  let domainScript: string = domainScriptValue;
  if (window.localStorage.getItem('oneTrustTestScript') === 'true' && domainScript.indexOf('test') === -1) {
    domainScript = domainScriptValue + '-test';
  }

  if (window.localStorage.getItem('ignoreOneTrustAutoblock') !== 'true') {
    const oneTrustAutoblockScript: HTMLScriptElement = window.document.createElement('script');
    oneTrustAutoblockScript.id = 'one-trust-script';
    oneTrustAutoblockScript.src = `https://cdn.cookielaw.org/consent/${domainScript}/OtAutoBlock.js`;
    head.insertBefore(oneTrustAutoblockScript, head.firstChild);
  }

  const oneTrustScript: HTMLScriptElement = window.document.createElement('script');
  oneTrustScript.id = 'one-trust-script';
  oneTrustScript.src = src;
  if (documentBasedLang) {
    oneTrustScript.setAttribute('data-document-language', 'true');
  }
  oneTrustScript.setAttribute('type', 'text/javascript');
  oneTrustScript.setAttribute('charset', 'UTF-8');
  oneTrustScript.setAttribute('data-domain-script', domainScript);
  head.insertBefore(oneTrustScript, head.firstChild);

  const oneTrustFuncScript = window.document.createElement('script');
  oneTrustFuncScript.type = 'text/javascript';
  oneTrustFuncScript.innerHTML = `function OptanonWrapper() { }`;
  head.insertBefore(oneTrustFuncScript, head.firstChild.nextSibling);
  // eslint-disable-next-line no-restricted-syntax
  console.info('OneTrust loaded');
};

export function loadOneTrust(
  domainScript: string,
  defaultCountry = 'de-AT',
  src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
): void {
  // Get ready to receive the OneTrust object once loaded
  OneTrustReady$()
    .pipe(take(1))
    .subscribe((oneTrust: OneTrust): void => {
      OneTrust$.next(oneTrust);
    });
  appliedLocale$.next(defaultCountry);
  initializeOneTrust(src, domainScript, false);

  // Creating a dummy element to hide blue OneTrust settings fixed button on the bottom left
  const div: HTMLDivElement = document.createElement('div');
  div.style.position = 'absolute';
  div.style.textIndent = '-9999px';
  div.classList.add('ot-sdk-show-settings');
  document.body.appendChild(div);
}
